import { useParams, useNavigate, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import { useBlog } from '../context/BlogContext';
import { useAuth } from '../context/AuthContext';
import CommentSection from '../components/CommentSection';
import PageTransition from '../components/PageTransition';
import SubscribeBox from '../components/SubscribeBox';
import ConfirmDialog from '../components/ConfirmDialog';
import { motion } from 'framer-motion';

export default function BlogPost() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { posts, loading, deletePost } = useBlog();
  const { user } = useAuth();
  const [scrollProgress, setScrollProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const post = posts.find((p) => p.id === id);

  // Prev / next post navigation
  const { prev, next } = useMemo(() => {
    const idx = posts.findIndex((p) => p.id === id);
    return {
      prev: idx > 0 ? posts[idx - 1] : null,
      next: idx < posts.length - 1 ? posts[idx + 1] : null,
    };
  }, [posts, id]);

  // Scroll progress tracker
  useEffect(() => {
    const onScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      setScrollProgress(docHeight > 0 ? Math.min(scrollTop / docHeight, 1) : 0);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (loading) return <div className="container" style={{ padding: '4rem 0', color: 'var(--text-tertiary)' }}>Loading...</div>;
  if (!post) return <div className="container" style={{ padding: '4rem 0' }}>Post not found.</div>;

  const readTime = post.readTime > 0 ? post.readTime : Math.max(2, Math.ceil((post.content || '').length / 800));
  const firstTag = (post.tags || [])[0] || '';

  const handleDelete = async () => {
    await deletePost(post.id);
    navigate('/');
  };

  const navBtnStyle = {
    width: '34px', height: '34px', borderRadius: '8px',
    border: '1px solid var(--border-subtle)', background: 'var(--bg-sidebar)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', color: 'var(--text-primary)',
    transition: 'all 0.2s',
  };

  const navBtnDisabled = {
    ...navBtnStyle,
    opacity: 0.3, cursor: 'default', pointerEvents: 'none',
  };

  return (
    <PageTransition>
      {/* Scroll progress bar */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: '3px', zIndex: 200,
        background: 'var(--border-light)',
      }}>
        <motion.div
          style={{
            height: '100%', background: 'var(--text-primary)',
            width: `${scrollProgress * 100}%`,
            transformOrigin: 'left',
          }}
        />
      </div>

      <div className="container" style={{ paddingTop: '2rem', paddingBottom: '4rem', maxWidth: '900px' }}>

        {/* Top bar: Back | tag + read time | prev/next */}
        <div className="post-topbar" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: '2rem',
        }}>
          {/* Back */}
          <button
            onClick={() => navigate('/')}
            style={{
              padding: '0.35rem 0.9rem', borderRadius: '8px',
              border: '1px solid var(--border-subtle)', background: 'var(--bg-sidebar)',
              fontSize: '0.82rem', fontFamily: 'var(--font-sans)',
              cursor: 'pointer', color: 'var(--text-primary)',
              transition: 'all 0.2s',
            }}
          >
            Back
          </button>

          {/* Center: tag + read time */}
          <div className="post-meta-pill" style={{
            padding: '0.35rem 1rem', borderRadius: '8px',
            border: '1px solid var(--border-subtle)',
            background: 'var(--bg-sidebar)',
            fontSize: '0.82rem', color: 'var(--text-secondary)',
            fontFamily: 'var(--font-sans)',
          }}>
            {firstTag}{firstTag && ' - '}{readTime} min read
          </div>

          {/* Prev / Next arrows */}
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            <button
              onClick={() => prev && navigate(`/post/${prev.id}`)}
              style={prev ? navBtnStyle : navBtnDisabled}
              title={prev ? prev.title : 'No previous post'}
              aria-label="Previous post"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>
            <button
              onClick={() => next && navigate(`/post/${next.id}`)}
              style={next ? navBtnStyle : navBtnDisabled}
              title={next ? next.title : 'No next post'}
              aria-label="Next post"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6" />
              </svg>
            </button>
          </div>
        </div>

        {/* Title */}
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-50px' }}
          transition={{ duration: 0.5 }}
          style={{
            fontFamily: 'var(--font-serif)', fontSize: 'clamp(2rem, 5vw, 3rem)',
            lineHeight: 1.12, fontWeight: 700, textAlign: 'center',
            marginBottom: '1.5rem', letterSpacing: '-0.02em',
          }}
        >
          {post.title}
        </motion.h1>

        {/* Excerpt / subtitle */}
        {post.excerpt && (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-50px' }}
            transition={{ duration: 0.5, delay: 0.1 }}
            style={{
              textAlign: 'center', color: 'var(--text-tertiary)',
              fontSize: '1.05rem', lineHeight: 1.6, maxWidth: '600px',
              margin: '0 auto 2.5rem',
            }}
          >
            {post.excerpt}
          </motion.p>
        )}

        {/* Hero image */}
        {post.image && (
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7, ease: [0.4, 0, 0.2, 1] }}
            className="post-hero-image-wrap"
            style={{ marginBottom: '3rem' }}
          >
            <img
              src={post.image}
              alt={post.title}
              className="post-hero-image"
            />
          </motion.div>
        )}

        {/* Admin actions */}
        {user?.role === 'admin' && (
          <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '2rem' }}>
            <Link to={`/admin/edit/${post.id}`} className="btn" style={{ padding: '0.3rem 0.9rem', fontSize: '0.78rem' }}>Edit</Link>
            <button onClick={() => setShowDeleteConfirm(true)} className="btn btn-danger" style={{ padding: '0.3rem 0.9rem', fontSize: '0.78rem' }}>Delete</button>
          </div>
        )}

        <ConfirmDialog
          open={showDeleteConfirm}
          title="Delete post"
          message="This post and all its comments will be permanently removed."
          confirmLabel="Delete"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />

        {/* Article body */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
          className="article-body rich-content"
          style={{
            fontSize: '1.08rem', color: 'var(--text-secondary)',
            lineHeight: 1.85,
          }}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />

        {/* Comments */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.5 }}
        >
          <CommentSection postId={post.id} comments={post.comments} />
        </motion.div>

        {/* Subscribe */}
        <SubscribeBox />
      </div>
    </PageTransition>
  );
}
